import { useMemo, useEffect } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from 'antd';
import TableHeaderDropdownSearch from '@/components/data-table/TableHeaderDropdownSearch';
import TableHeaderSort from '@/components/data-table/TableHeaderSort';
import TableHeaderTitle from '@/components/data-table/TableHeaderTitle';
import TruncateTooltipCell from '@/components/data-table/TruncateTooltipCell';
import UserStatus from '@/components/user/UserStatus';
import useAppDataContext from '@/contexts/app-data/useAppDataContext';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { EMPLOYEE_HR_DOCUMENTS_COLUMNS_DEFAULT_PARAMS } from '@/shared/employee';
import { formatHexColor } from '@/utils/formUtils';
import { formatDateTimeWithLocale } from '@/utils/utils';
import HRDocumentsActions from './HRDocumentsActions';

export default function HRDocumentsColumns() {
  const { t } = useTranslation(['tables', 'forms', 'messages']);

  const { queryParams, setQueryParams } = useQueryParamsContext();

  const { enums: enumsData } = useAppDataContext();
  const [searchTerm, setSearchTerm] = useState(
    EMPLOYEE_HR_DOCUMENTS_COLUMNS_DEFAULT_PARAMS
  );

  useEffect(() => {
    const status = queryParams?.status
      ? Array.isArray(queryParams.status)
        ? queryParams.status
        : [queryParams.status]
      : undefined;

    const typeOfLeave = queryParams?.typeOfLeave
      ? Array.isArray(queryParams.typeOfLeave)
        ? queryParams.typeOfLeave
        : [queryParams.typeOfLeave]
      : undefined;

    setSearchTerm({
      firstName: queryParams?.firstName || '',
      lastName: queryParams?.lastName || '',
      status: status,
      typeOfLeave: typeOfLeave,
    });
  }, [queryParams]);

  const [toggleDropdown, setToggleDropdown] = useState({
    firstName: false,
    lastName: false,
    status: false,
    typeOfDocument: false,
  });

  const handleSearchTerm = (name, value) => {
    setSearchTerm((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleClose = (name, value) => {
    setToggleDropdown((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleCheckboxReset = (name) => {
    setQueryParams({ [name]: undefined, offset: 1 });

    setSearchTerm((prev) => {
      return {
        ...prev,
        [name]: [],
      };
    });

    handleClose(name, false);
  };

  const handleSubmit = (name, form) => {
    const value = form[name];

    handleClose(name, false);

    if (value && value?.length > 0) {
      setQueryParams({ [name]: value, offset: 1 });
    } else {
      setQueryParams({ [name]: undefined });
    }
  };

  const workTypesOptions = useMemo(() => {
    const options = enumsData?.companies?.workTypes.length
      ? enumsData?.companies?.workTypes
          .map((item) => {
            return {
              ...item,
              value: item.value,
              createsDocument: item.createsDocument,
              hrDropdown: item.hrDropdown,
            };
          })
          .filter(
            (item) =>
              (item.createsDocument || item.hrDropdown) &&
              item.value !== 'paidLeave' &&
              item.value !== 'otherLeave'
          )
      : [];

    const uploadedOption = {
      value: 'UPLOADED',
      label: t('uploadedFiles', { ns: 'messages' }),
    };

    return [uploadedOption, ...options];
  }, [t, enumsData?.companies?.workTypes]);

  const statusOptions = useMemo(() => {
    const options = enumsData?.requests?.status.length
      ? enumsData?.requests?.status
          .map((item) => {
            return {
              ...item,
              value: item?.value.toLowerCase(),
            };
          })
          .filter(
            (item) => !['waiting', 'submitted', 'deleted'].includes(item.value)
          )
      : [];

    return options;
  }, [enumsData?.requests?.status]);

  return [
    {
      title: () => {
        return (
          <TableHeaderTitle title={t('typeOfDocument', { ns: 'tables' })}>
            <TableHeaderDropdownSearch
              dataIndex="typeOfLeave"
              title={t('typeOfDocument', { ns: 'tables' })}
              searchTerm={searchTerm}
              toggleDropdown={toggleDropdown}
              options={workTypesOptions}
              onChange={handleSearchTerm}
              onReset={handleCheckboxReset}
              onClose={handleClose}
              onSubmit={handleSubmit}
            />

            <TableHeaderSort dataIndex="typeOfLeave" />
          </TableHeaderTitle>
        );
      },
      dataIndex: '',
      render: (record) => {
        const status = record?.status;

        if (!status) {
          const label = record?.title || record?.file.title || '~';

          return <TruncateTooltipCell label={label || '~'} maxLength={48} />;
        }

        if (record?.typeOfLeave) {
          const { title = '' } = record;
          const { label = '-', color, textColor } = record.typeOfLeave;
          const tagColor = formatHexColor(color?.argb);

          return (
            <div className="grid gap-1">
              <Tag
                color={tagColor}
                style={{
                  color: textColor,
                  marginInlineEnd: 4,
                  borderRadius: 50,
                  width: 'fit-content',
                }}
              >
                {label}
              </Tag>

              {title && <p>{title}</p>}
            </div>
          );
        } else {
          const label = record?.title || '';

          return <TruncateTooltipCell label={label || '~'} maxLength={48} />;
        }
      },
    },
    {
      title: () => {
        return (
          <TableHeaderTitle title={t('status', { ns: 'tables' })}>
            <TableHeaderDropdownSearch
              dataIndex="status"
              title={t('status', { ns: 'tables' })}
              searchTerm={searchTerm}
              toggleDropdown={toggleDropdown}
              options={statusOptions}
              onChange={handleSearchTerm}
              onReset={handleCheckboxReset}
              onClose={handleClose}
              onSubmit={handleSubmit}
            />

            <TableHeaderSort dataIndex="status" />
          </TableHeaderTitle>
        );
      },
      dataIndex: 'status',
      render: (_, record) => {
        if (record.type === 'UPLOADED') {
          return (
            <img
              alt="File"
              src={`/assets/images/file.png`}
              className="mx-auto block h-6 w-6 shadow-xl"
            />
          );
        }

        return <UserStatus status={record.status} />;
      },
    },
    {
      title: () => {
        return (
          <TableHeaderTitle title={t('createdAt', { ns: 'tables' })}>
            <TableHeaderSort dataIndex="createdAt" />
          </TableHeaderTitle>
        );
      },
      dataIndex: 'createdAt',
      render: (record) => {
        return formatDateTimeWithLocale(
          record,
          {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          },
          true
        );
      },
    },
    {
      title: t('actions', { ns: 'tables' }),
      render: (record) => {
        return <HRDocumentsActions record={record} />;
      },
    },
  ];
}
